import {
  Box,
  CircularProgress,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
} from "@mui/material";
import { capitalize } from "lodash";
import Flex from "../../../../../components/Flex";
import StatusChip from "../../../../../components/StatusChip";
import StyledDialog from "../../../../../components/StyledDialog";
import Text from "../../../../../components/Text";
import { NonNullableDate } from "../../../../../utils/date-time-utility";
import {
  getFormattedId,
  replaceUnderScoreAddSpace,
} from "../../../../../utils/utils";

// View of a denied CPT line in the liteWeight payload. Per ECRH-5354, the
// backend exposes the CARC denial code as `denialCode` and the denial
// description as `comments`; a few legacy aliases are kept as defensive
// fallbacks (mirrors the LiteCpt handling in claim-cpt-breakdown.ts).
interface DeniedCptLine {
  cptCode?: string;
  procedureCode?: Record<string, string> | string;
  dateOfService?: string;
  denied?: boolean;
  // CARC denial code
  denialCode?: string;
  carc?: string;
  carcCode?: string;
  // Human-readable denial reason / description
  comments?: string;
  denialDescription?: string;
  denialReason?: string;
  description?: string;
  status?: string;
  cptPostingStatus?: string;
}

type ClaimDenialDetail = {
  claimId?: string;
  status?: string;
  patientName?: string;
  providerName?: string;
  dateOfService?: string;
  insurancePayer?: {
    payerId?: string;
    payerName?: string;
  } | null;
  insuranceType?: string;
  encounterId?: string;
  cptCodes?: DeniedCptLine[];
};

interface ViewDenialModalProps {
  open: boolean;
  onClose: () => void;
  data?: ClaimDenialDetail | null;
  loading?: boolean;
  // Date of Service is not part of the liteWeight payload; it comes from the
  // claim grid row.
  dateOfService?: string;
}

const text = (value?: string | null) => (value ? value : "-");

// Derive the CPT code from either a `{ "96373": "..." }` map, a plain string,
// or an explicit `cptCode` field.
const extractCpt = (line: DeniedCptLine): string => {
  if (line?.cptCode) return line.cptCode;
  const code = line?.procedureCode;
  if (typeof code === "string") return code;
  const obj = code ?? {};
  const entry = (Object.values(obj)[0] as string) ?? "";
  const [cpt = ""] = entry.split(" - ");
  return cpt.trim() || Object.keys(obj)[0] || "-";
};

const extractCarc = (line: DeniedCptLine): string =>
  text(line?.denialCode ?? line?.carc ?? line?.carcCode);

const extractDenialDescription = (line: DeniedCptLine): string =>
  text(
    line?.comments ??
      line?.denialDescription ??
      line?.denialReason ??
      line?.description
  );

const extractCptStatus = (line: DeniedCptLine): string =>
  text(line?.status ?? line?.cptPostingStatus);

const DetailRow = ({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) => (
  <Flex
    justifyContent="space-between"
    alignItems="center"
    gap="16px"
    sx={{ py: "6px" }}
  >
    <Text isTabletext color="text.secondary">
      {label}
    </Text>
    <Box sx={{ textAlign: "right" }}>
      {typeof children === "string" ? (
        <Text isTabletext>{children}</Text>
      ) : (
        children
      )}
    </Box>
  </Flex>
);

const ViewDenialModal = ({
  open,
  onClose,
  data,
  loading,
  dateOfService,
}: ViewDenialModalProps) => {
  const deniedCpts = (data?.cptCodes ?? []).filter(
    // A denied claim's denial list should surface only denied CPT lines; if the
    // backend doesn't flag them individually, fall back to showing every line.
    (line) =>
      line?.denied === true ||
      line?.status === "DENIED" ||
      !!(line?.denialCode ?? line?.carc ?? line?.carcCode)
  );
  const cptRows = deniedCpts.length ? deniedCpts : (data?.cptCodes ?? []);
  const dos = dateOfService ?? data?.dateOfService;

  return (
    <StyledDialog
      open={open}
      onClose={onClose}
      dialogTitle="Denial Details"
      PaperProps={{ sx: { width: "48rem", maxWidth: "95vw" } }}
    >
      {loading ? (
        <Flex justifyContent="center" alignItems="center" sx={{ py: 6 }}>
          <CircularProgress size={28} />
        </Flex>
      ) : !data ? (
        <Flex justifyContent="center" sx={{ py: 6 }}>
          <Text isTabletext color="text.secondary">
            No denial details available.
          </Text>
        </Flex>
      ) : (
        <Box>
          {/* Claim-level details shared by every denied CPT below. */}
          <DetailRow label="Claim Number">
            {data?.claimId ? getFormattedId(data.claimId) : "-"}
          </DetailRow>
          <DetailRow label="Patient Name">{text(data?.patientName)}</DetailRow>
          <DetailRow label="Date of Service">
            {dos ? NonNullableDate(dos as any) : "-"}
          </DetailRow>
          <DetailRow label="Payer">
            {text(data?.insurancePayer?.payerName)}
          </DetailRow>
          <DetailRow label="Current Claim Status">
            {data?.status ? (
              <StatusChip
                label={capitalize(replaceUnderScoreAddSpace(data.status))}
                variant="error"
              />
            ) : (
              "-"
            )}
          </DetailRow>

          <Divider sx={{ my: "12px" }} />

          <Text fontWeight="medium" sx={{ mb: "8px" }}>
            Denied CPT Codes
          </Text>

          {cptRows.length === 0 ? (
            <Flex justifyContent="center" sx={{ py: 3 }}>
              <Text isTabletext color="text.secondary">
                No denied CPT codes for this claim.
              </Text>
            </Flex>
          ) : (
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>
                    <Text isTabletext fontWeight="medium">
                      CPT Code
                    </Text>
                  </TableCell>
                  <TableCell>
                    <Text isTabletext fontWeight="medium">
                      Denial Code (CARC)
                    </Text>
                  </TableCell>
                  <TableCell>
                    <Text isTabletext fontWeight="medium">
                      Denial Description
                    </Text>
                  </TableCell>
                  <TableCell>
                    <Text isTabletext fontWeight="medium">
                      Status
                    </Text>
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {cptRows.map((line, index) => {
                  const cptStatus = extractCptStatus(line);
                  return (
                    <TableRow key={`${extractCpt(line)}__${index}`}>
                      <TableCell>
                        <Text isTabletext>{extractCpt(line)}</Text>
                      </TableCell>
                      <TableCell>
                        <Text isTabletext>{extractCarc(line)}</Text>
                      </TableCell>
                      <TableCell>
                        <Text isTabletext>
                          {extractDenialDescription(line)}
                        </Text>
                      </TableCell>
                      <TableCell>
                        {cptStatus !== "-" ? (
                          <StatusChip
                            label={capitalize(
                              replaceUnderScoreAddSpace(cptStatus)
                            )}
                            variant={
                              cptStatus === "DENIED" ? "error" : "default"
                            }
                          />
                        ) : (
                          <Text isTabletext>-</Text>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </Box>
      )}
    </StyledDialog>
  );
};

export default ViewDenialModal;
